import { Fragment, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { IconName } from 'ui/components'
import { IconButton } from 'ui/components/Buttons'
import { useConfirmation } from 'ui/hooks'
import { Col, Col2x, FormRow } from 'ui/themes/globalStyles'
import { emptySAR } from '~bank-bsa-reporting/constants'
import { SarEdit } from '~bank-bsa-reporting/types'

import {
  Accountant,
  ActionDate,
  Agent,
  Appraiser,
  Attorney,
  Borrower,
  Customer,
  Director,
  Employee,
  NoRelationship,
  Officer,
  Other,
  OtherDesk,
  Owner,
  RelationshipStatus,
  TIN
} from './fields'

import { s } from './styles'

export const RelationshipsInfo = ({ index }: { index: number }) => {
  const { control } = useFormContext<SarEdit>()
  const [itemToRemove, setItemToRemove] = useState<number | null>(null)

  const { fields, append, remove } = useFieldArray({
    control,
    name: `subjectsInfo.${index as 0}.relationshipsInfo`
  })

  const { open: startDeleting, Confirmation } = useConfirmation({
    message: `Are you sure you want to remove this item?`,
    onConfirm: () => itemToRemove !== null && remove(itemToRemove),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  return (
    <>
      <FormRow>
        <Col2x>
          <h5>24. Relationship of the subject to an institution listed in Part III or IV</h5>
        </Col2x>
      </FormRow>
      {fields.map((field, index2) => (
        <Fragment key={field.id}>
          <RelationshipsInfoItem index1={index} index2={index2} />
          <FormRow>
            {fields.length > 1 && (
              <IconButton
                face="negative"
                icon={IconName.DELETE}
                onClick={() => {
                  setItemToRemove(index2)
                  startDeleting()
                }}
              />
            )}
          </FormRow>
        </Fragment>
      ))}

      <Confirmation />
      <s.AddButton>
        <IconButton icon={IconName.ADD_GREEN} onClick={() => append(emptySAR.subjectsInfo[0].relationshipsInfo[0])} />
      </s.AddButton>
    </>
  )
}

const RelationshipsInfoItem = ({ index1, index2 }: { index1: number; index2: number }) => {
  const { control } = useFormContext<SarEdit>()
  const other = useWatch({ control, name: `subjectsInfo.${index1 as 0}.relationshipsInfo.${index2 as 0}.other` })

  return (
    <>
      <FormRow>
        <Col>
          <TIN index1={index1} index2={index2} />
        </Col>
      </FormRow>
      <FormRow>
        <Col>
          <s.CheckBoxWrapper>
            <Accountant index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Agent index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Appraiser index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Attorney index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Borrower index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Customer index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
        </Col>
        <Col>
          <s.CheckBoxWrapper>
            <Director index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Employee index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <NoRelationship index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Officer index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Owner index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.CheckBoxWrapper>
            <Other index1={index1} index2={index2} />
          </s.CheckBoxWrapper>
          <s.VisibilityBox $visible={other}>
            <OtherDesk index1={index1} index2={index2} />
          </s.VisibilityBox>
        </Col>
      </FormRow>
      <FormRow>
        <Col>
          <RelationshipStatus index1={index1} index2={index2} />
        </Col>
        <Col>
          <ActionDate index1={index1} index2={index2} />
        </Col>
      </FormRow>
    </>
  )
}
